'use client'

import { Check, ChevronDown, Search } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { comboboxOptionClass } from '@/components/widget/combobox-option'
import { isHedgeableInstrument, underlyingOf } from '@/features/hedge/lib/hedge-instrument'
import { instrumentKindTag, instrumentRank } from '@/features/hedge/lib/instrument-display'
import { useCanAutoFocusSearch } from '@/hooks/use-screen'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { Instrument } from '@/types/generated/common/types'

interface HedgeInstrumentSelectProps {
  value: string
  // The override is always scoped to one underlying; only that underlying's spot/perp
  // variants are offered (never options or dated futures).
  underlying: string
  onChange: (value: string) => void
  // 'inline' renders the trigger as pressable text (e.g. embedded in a summary sentence)
  // instead of the default full-width combobox button (hedge-instrument-field.tsx's).
  variant?: 'default' | 'inline'
  // Trigger text. Falls back to `value` when omitted.
  label?: string
  // When set and an override is active, a "Use default" row above the list clears it.
  defaultLabel?: string
  // Every exchange's instruments flattened together — a hedge instrument can come from
  // any exchange a position is on, not just the currently-focused card's own exchange.
  instruments: Instrument[]
  // 'default' variant only: replaces the trigger's surface, as in HedgeUnderlyingSelect.
  fieldClassName?: string
}

export function HedgeInstrumentSelect({
  value,
  underlying,
  onChange,
  variant = 'default',
  label,
  defaultLabel,
  instruments,
  fieldClassName,
}: HedgeInstrumentSelectProps) {
  const canAutoFocusSearch = useCanAutoFocusSearch()
  const t = useTranslations('orderForm')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const activeItemRef = useRef<HTMLButtonElement>(null)
  // Portal the dropdown into the surrounding dialog (when there is one) so it lives inside the
  // dialog's focus scope + scroll lock. Portaled to document.body instead, the modal swallows
  // Tab navigation and wheel-scroll inside the popover.
  const [container, setContainer] = useState<HTMLElement | null>(null)

  const sorted = useMemo(() => {
    const u = underlying.toUpperCase()
    return instruments
      .filter(
        (i) =>
          i.isActive &&
          isHedgeableInstrument(i) &&
          (i.baseCurrency || underlyingOf(i.symbol)).toUpperCase() === u
      )
      .sort((a, b) => instrumentRank(a) - instrumentRank(b) || a.symbol.localeCompare(b.symbol))
  }, [instruments, underlying])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? sorted.filter((i) => i.symbol.toLowerCase().includes(q)) : sorted
  }, [sorted, query])

  // Re-highlight the top match whenever the query changes.
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keep the highlighted row scrolled into view as arrow keys move it.
  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // Radix listens for Escape on `document` in the capture phase, and with the popover
  // portaled into the dialog the dialog's handler can win — closing the whole hedge dialog.
  // While the dropdown is open, intercept Escape one level higher (window capture fires
  // before document) to close only the dropdown and stop the key from going further.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      setOpen(false)
      setQuery('')
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [open])

  const choose = (symbol: string) => {
    onChange(symbol)
    setQuery('')
    setOpen(false)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const sel = filtered[activeIndex]
      if (sel) choose(sel.symbol)
    }
  }

  const activeId = filtered[activeIndex]
    ? `hedge-instrument-${filtered[activeIndex].internalId}`
    : undefined

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setContainer(triggerRef.current?.closest('[role="dialog"]') as HTMLElement | null)
        } else {
          setQuery('')
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        {variant === 'inline' ? (
          <button
            ref={triggerRef}
            type="button"
            className="text-foreground hover:text-foreground/70 decoration-foreground/40 cursor-pointer underline decoration-dotted underline-offset-2 transition-colors"
          >
            {formatSymbol(label || value) || t('selectInstrument')}
          </button>
        ) : (
          // Same trigger as HedgeUnderlyingSelect (the Target field) beside it. With no
          // override it names the default perpetual it will use, tagged as the default.
          <button
            ref={triggerRef}
            type="button"
            className={cn(
              'flex w-full items-center justify-between gap-2 data-[state=open]:[&>svg]:rotate-180',
              fieldClassName ??
                'bg-foreground/10 data-[state=open]:bg-foreground/20 h-8 rounded-sm px-2.5 py-1 text-xs'
            )}
          >
            <span className={cn('truncate', !(label || value) && 'text-muted-foreground')}>
              {formatSymbol(label || value) || t('selectInstrument')}
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              {!value && defaultLabel && (
                <span className="text-foreground/40 text-[10px]">{t('defaultTag')}</span>
              )}
              <ChevronDown className="size-3.5 shrink-0 opacity-60 transition-transform" />
            </span>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        container={container ?? undefined}
        // The 'inline' trigger is just pressable text (e.g. "BTC-PERPETUAL"), far narrower
        // than a dropdown needs to be — matching its width (as the 'default' full-width
        // button trigger still does, via the CSS var below) clipped every instrument name in
        // the list. Give it a fixed comfortable width instead, independent of the trigger.
        className={cn('p-0', variant === 'inline' && 'w-72')}
        style={variant === 'inline' ? undefined : { width: 'var(--radix-popover-trigger-width)' }}
      >
        <div className="border-foreground/10 flex items-center gap-2 border-b px-3 py-2">
          <Search className="text-muted-foreground size-3.5 shrink-0" />
          <input
            autoFocus={canAutoFocusSearch}
            role="combobox"
            aria-expanded
            aria-controls="hedge-instrument-list"
            aria-activedescendant={activeId}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={t('searchInstruments')}
            className="placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
          />
        </div>
        <div id="hedge-instrument-list" role="listbox" className="max-h-60 overflow-y-auto p-1">
          {defaultLabel && value && (
            <button
              type="button"
              onClick={() => choose('')}
              className="text-muted-foreground hover:bg-foreground/5 border-foreground/10 mb-1 flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-xs"
            >
              Use default ({formatSymbol(defaultLabel)})
            </button>
          )}
          {filtered.length === 0 ? (
            <p className="text-muted-foreground px-2 py-4 text-center text-xs">
              {sorted.length === 0
                ? t('noHedgeInstruments', { underlying: underlying || t('thisUnderlying') })
                : t('noMatches')}
            </p>
          ) : (
            filtered.map((i, idx) => {
              const isSelected = i.symbol === value
              const isActive = idx === activeIndex
              return (
                <button
                  key={i.internalId}
                  id={`hedge-instrument-${i.internalId}`}
                  ref={isActive ? activeItemRef : undefined}
                  role="option"
                  aria-selected={isSelected}
                  type="button"
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => choose(i.symbol)}
                  className={comboboxOptionClass({ active: isActive, selected: isSelected })}
                >
                  <span className="truncate">{formatSymbol(i.symbol)}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-muted-foreground text-[10px]">
                      {instrumentKindTag(i)}
                    </span>
                    {isSelected && <Check className="text-chart-mint size-3" />}
                  </span>
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
